import { useState, useEffect, useRef } from 'react';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { useAppStore } from '../store/useAppStore';

interface TokenInputProps {
  value?: string;
  onChange?: (address: string) => void;
  onSubmit?: (address: string) => void;
}

export default function TokenInput({ value, onChange, onSubmit }: TokenInputProps) {
  const { selectedAddress, setSelectedAddress } = useAppStore();
  const [internalValue, setInternalValue] = useState('');
  const useStore = value === undefined && onChange === undefined;
  const tokenAddress = useStore ? selectedAddress : (value ?? internalValue);
  const setTokenAddress = useStore ? setSelectedAddress : (onChange ?? setInternalValue);

  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  const { history, deleteFromHistory } = useSearchHistory();

  // Handle clicks outside history modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        historyRef.current &&
        !historyRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowHistory(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Validate Solana address
  const validateSolanaAddress = (address: string): boolean => {
    // Check for empty value
    if (!address.trim()) {
      setError('Please enter a token address');
      return false;
    }

    // Check length (32-44 characters)
    if (address.length < 32 || address.length > 44) {
      setError('Invalid address length (32-44 characters required)');
      return false;
    }

    // Check Base58 characters (excludes 0, O, I, l)
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    if (!base58Regex.test(address)) {
      setError('Invalid characters detected');
      return false;
    }

    setError('');
    return true;
  };

  // Submit handler (history is saved in App after API success)
  const handleSubmit = () => {
    if (validateSolanaAddress(tokenAddress)) {
      setShowHistory(false);
      onSubmit?.(tokenAddress);
    }
  };

  // Enter key handler
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <>
      <div className="input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className={`token-input ${error ? 'error' : ''}`}
          placeholder="e.g., AVF9F4C4j8b1Kh4BmNHqybDaHgnZpJ7W7yLvL7hUpump"
          value={tokenAddress}
          onChange={(e) => setTokenAddress(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowHistory(true)}
          aria-label="Enter Solana token address"
        />
        <button
          className="submit-button"
          onClick={handleSubmit}
          aria-label="Submit"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 12H19M19 12L12 5M19 12L12 19"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Search History Dropdown */}
        {showHistory && history.length > 0 && (
          <div ref={historyRef} className="history-dropdown">
            {history.map((item) => (
              <div
                key={item.timestamp}
                className="history-item"
                onClick={() => {
                  setTokenAddress(item.address);
                  setShowHistory(false);
                  if (validateSolanaAddress(item.address)) {
                    onSubmit?.(item.address);
                  }
                }}
              >
                <div className="history-item-content">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="history-thumbnail"
                      width={24}
                      height={24}
                    />
                  ) : (
                    <svg
                      className="history-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  <div className="history-text">
                    {item.symbol && (
                      <span className="history-symbol">{item.symbol}</span>
                    )}
                    <span className="history-address">{item.address}</span>
                  </div>
                </div>
                <button
                  className="delete-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteFromHistory(item.address);
                  }}
                  aria-label="Delete from history"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <p className="error-message" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
